import { css } from '@emotion/css'
import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import Swal from 'sweetalert2'
import { useCustomReducer } from '../../../../../base/customHooks'
import { getUploadFileRequest } from '../../../../../base/utils/dataHelper'
import IPButton from '../../../../../components/IPButton'
import IPCheckbox from '../../../../../components/IPCheckBox'
import IPFile from '../../../../../components/IPFile'
import IPInput from '../../../../../components/IPInput'
import IPModal from '../../../../../components/IPModal'
import IPSelectBox from '../../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import InputCampaign from '../../../../../components/InputCampaign'
import Table from '../../../../../components/Table'
import {
  ExcelAnnouncementGroupMode,
  ExcelAnnouncementImagePosition,
  ExcelAnnouncementRepeatMode,
  ExcelAnnouncementRowGroupBy,
  IExcelAnnouncement,
  IExcelAnnouncementImportReport,
  IExcelAnnouncementRow,
  IExcelAnnouncementRowGroup,
  IUploadFile,
  groupByOptions,
  groupModeOptions,
  imagePositionOptions,
  repeatModeOptions,
} from '../models/excelAnnouncementModels'
import { ExcelAnnouncementService } from '../services/excelAnnouncementService'

interface IExcelAnnouncementState {
  showRows: boolean
  showPreview: boolean

  editId: number
  topic: string
  description: string
  startDate: Date
  endDate: Date
  enable: boolean
  repeatMode: ExcelAnnouncementRepeatMode
  groupMode: ExcelAnnouncementGroupMode
  imagePosition: ExcelAnnouncementImagePosition
  hasHeaderRow: boolean
  showGuaranteeLetters: boolean

  excelFile?: IUploadFile
  excelFileName?: string
  imageFile?: IUploadFile
  imageFileName?: string
  removeImage: boolean

  /** Seçilen Excel'in kolon harfleri ve ilk satır örnekleri; #B# neye karşılık geliyor görünsün. */
  columnHints: { column: string; sample: string }[]

  groupBy: ExcelAnnouncementRowGroupBy
  selectedGroupKey?: string
  previewRow?: IExcelAnnouncementRow

  /** Her "bayi verilerini goster" isteginde artar; bolum zaten acikken de kaydirmayi tetikler. */
  rowsRequestId: number
}

const initialState = (): IExcelAnnouncementState => ({
  showRows: false,
  showPreview: false,
  editId: 0,
  topic: '',
  description: '',
  startDate: new Date(),
  endDate: moment().add(7, 'days').toDate(),
  enable: true,
  repeatMode: ExcelAnnouncementRepeatMode.Once,
  groupMode: ExcelAnnouncementGroupMode.Separate,
  imagePosition: ExcelAnnouncementImagePosition.Above,
  hasHeaderRow: false,
  showGuaranteeLetters: false,
  removeImage: false,
  columnHints: [],
  groupBy: ExcelAnnouncementRowGroupBy.Announcement,
  rowsRequestId: 0,
})

/**
 * Form alanlarını daraltan sınıflar. InputCampaign kendi sarmalayıcısına sabit 50px
 * yükseklik veriyor; dışarıdan prop'la değişmediği için alt seçiciyle eziliyor.
 */
const compactDateField = css`
  max-width: 215px;

  > div {
    height: 38px;
  }

  input {
    margin-top: 9px;
    font-size: 11px;
    padding: 0 0.6rem;
  }

  label {
    left: 0.6rem;
    font-size: 10px;
  }
`

/** Açılır listeler: kapsayıcıyı taşmasınlar diye body'ye portallanıyor, bir de alçaltılıyor. */
const compactSelectStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  control: (base: any) => ({ ...base, minHeight: '38px', fontSize: '13px' }),
  menu: (base: any) => ({ ...base, fontSize: '13px' }),
  valueContainer: (base: any) => ({ ...base, padding: '0 8px' }),
}

function selected(options: { value: any; label: string }[], value: any): ISelectOption | undefined {
  const match = options.find(option => option.value === value)
  return match ? ({ value: match.value, label: match.label } as unknown as ISelectOption) : undefined
}

/** InputCampaign'in datetime-local'i YYYY-MM-DDTHH:mm:ss bekler; sv-SE zaten bu düzende yazar. */
function toDateTimeLocal(date: Date): string {
  if (!date) return ''

  return date
    .toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    .replace(' ', 'T')
}

/**
 * Excel seri numarasını gün.ay.yıl (saat varsa + SS:dd) metnine çevirir.
 * Sunucudaki ExcelAnnouncementSheetReader ile aynı kurallar:
 *   - saniyeye yuvarlanır — dosyada 46561 yerine 46560,99999998 saklanmış olabiliyor
 *     ve tabana yuvarlamak bir gün geriye, 23:59'a düşürüyordu;
 *   - saat bileşeni yoksa saat yazılmaz.
 * Hesap baştan sona UTC'dir; yerel saat dilimi sonuca karışmaz.
 */
function excelSerialToText(serial: number): string {
  const totalSeconds = Math.round(serial * 86400)
  const days = Math.floor(totalSeconds / 86400)
  const secondsInDay = totalSeconds - days * 86400

  // Excel'in çıpası 30.12.1899'dur; 1900'ü artık yıl sayan hatasını da bu karşılar.
  const date = new Date(Date.UTC(1899, 11, 30))
  date.setUTCDate(date.getUTCDate() + days)

  const pad = (value: number) => String(value).padStart(2, '0')
  const day = `${pad(date.getUTCDate())}.${pad(date.getUTCMonth() + 1)}.${date.getUTCFullYear()}`

  if (secondsInDay === 0) return day

  return `${day} ${pad(Math.floor(secondsInDay / 3600))}:${pad(Math.floor((secondsInDay % 3600) / 60))}`
}

/**
 * Excel ile bayi bazlı duyuru sayfası. Duyuru Listesi'nden buraya geçilir; normal
 * duyuru akışıyla kesişmez. Bu duyurular bayinin ana sayfasında popup olarak çıkar
 * ve açbox listesine düşer; mobil bildirim gönderilmez.
 */
export default function ExcelAnnouncement() {
  const [state, setState] = useCustomReducer<IExcelAnnouncementState>(initialState())
  const [announcements, setAnnouncements] = useState<IExcelAnnouncement[]>([])
  const [groups, setGroups] = useState<IExcelAnnouncementRowGroup[]>([])
  const [rows, setRows] = useState<IExcelAnnouncementRow[]>([])
  const [report, setReport] = useState<IExcelAnnouncementImportReport | undefined>()

  /** Kolon rehberini yeniden okuyabilmek için seçilen dosyanın kendisi. Sunucuya bundan gitmiyor. */
  const [rawExcel, setRawExcel] = useState<File | undefined>()

  /** Bayi verileri bölümü sayfanın altında açılıyor; açılınca oraya kaydırıyoruz. */
  const rowsSectionRef = useRef<HTMLDivElement>(null)

  const service = () => new ExcelAnnouncementService({})

  const showError = (err: any) =>
    Swal.fire({ icon: 'error', title: err?.description ?? 'İşlem başarısız.', allowOutsideClick: true })

  const loadAnnouncements = () => {
    service()
      .getFiltered({})
      .then(result => setAnnouncements(result ?? []))
      .catch(showError)
  }

  useEffect(() => {
    loadAnnouncements()
  }, [])

  // Bolum acildiktan SONRA kaydiriyoruz; render'dan once ref bos olur.
  useEffect(() => {
    if (state.rowsRequestId > 0 && state.showRows)
      rowsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [state.rowsRequestId, state.showRows])

  // -------------------------------------------------------------- Dosya seçimi

  const onExcelSelected = async (event: any) => {
    const files = await getUploadFileRequest(event)
    if (!files || files.length === 0) return

    const file = event.target.files[0] as File
    setState({ excelFile: files[0] as IUploadFile, excelFileName: file?.name })
    setRawExcel(file)
  }

  // Rehberi tek yerden okuyoruz: dosya seçilince ve başlık satırı kutusu değişince.
  // Kutu dosyadan sonra işaretlenirse örnek bir satır kayar ve başlık öneki kazanır;
  // eski rehber ekranda kalmamalı.
  useEffect(() => {
    if (rawExcel) readColumnHints(rawExcel, state.hasHeaderRow)
  }, [state.hasHeaderRow, rawExcel])

  /**
   * Kolon rehberi yalnız ekranda göstermek için tarayıcıda okunur. Duyuruyu üreten
   * ayrıştırma sunucuda yapılır; buradaki okuma hiçbir şeyi kaydetmez.
   */
  const readColumnHints = async (file: File, hasHeaderRow: boolean) => {
    try {
      const XLSX = await import('xlsx')
      const buffer = await file.arrayBuffer()
      // cellNF: hücrenin sayı biçimi (z) gelsin — tarih hücresini ondan tanıyoruz.
      // cellDates KULLANILMIYOR: SheetJS'in ürettiği Date, yerel saat dilimi çıpası
      // yüzünden kayıyor (İstanbul'un 1900 öncesi LMT farkı), tam seride bile bir gün
      // geriye düşüyordu. Seri numarasını aşağıda kendimiz çeviriyoruz.
      const workbook = XLSX.read(buffer, { type: 'array', cellNF: true })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      const reference = sheet?.['!ref']

      if (!reference) {
        setState({ columnHints: [] })
        return
      }

      const range = XLSX.utils.decode_range(reference)
      const headerRowIndex = range.s.r
      const sampleRowIndex = hasHeaderRow ? headerRowIndex + 1 : headerRowIndex

      const cellAt = (row: number, column: number) =>
        row > range.e.r ? undefined : sheet[XLSX.utils.encode_cell({ r: row, c: column })]

      const hints: { column: string; sample: string }[] = []
      for (let column = range.s.c; column <= range.e.c; column++) {
        const header = hasHeaderRow ? cellAt(headerRowIndex, column) : undefined
        const label = header?.v !== undefined && header.v !== null ? `${header.v}: ` : ''

        hints.push({
          column: XLSX.utils.encode_col(column),
          sample: `${label}${cellSample(cellAt(sampleRowIndex, column), XLSX)}`,
        })
      }

      setState({ columnHints: hints })
    } catch {
      // Rehber gösterilemezse form yine çalışır; ayrıştırma sunucuda yapılıyor.
      setState({ columnHints: [] })
    }
  }

  /** Hücrenin ekranda görüneceği metin. Tarihler sunucuyla aynı biçimde yazılır. */
  const cellSample = (cell: any, XLSX: any): string => {
    if (!cell || cell.v === undefined || cell.v === null) return ''

    if (cell.t === 'n' && cell.z && XLSX.SSF.is_date(cell.z)) return excelSerialToText(cell.v)

    // Biçimlenmiş metin (tutarda binlik ayracı gibi) varsa o, yoksa ham değer.
    return String(cell.w ?? cell.v)
  }

  const onImageSelected = async (event: any) => {
    const files = await getUploadFileRequest(event)
    if (files && files.length > 0)
      setState({ imageFile: files[0] as IUploadFile, imageFileName: event.target.files[0]?.name, removeImage: false })
  }

  // -------------------------------------------------------------- Kaydetme

  /** Formu boşaltır. Yükleme raporuna dokunmaz — kaydetmenin ardından o rapor okunacak. */
  const resetForm = () => {
    setRawExcel(undefined)
    setState({ ...initialState(), showRows: state.showRows })
  }

  const openEdit = (announcement: IExcelAnnouncement) => {
    setReport(undefined)
    setState({
      editId: announcement.Id,
      topic: announcement.Topic,
      description: announcement.DescriptionTemplate,
      startDate: new Date(announcement.StartDate),
      endDate: new Date(announcement.EndDate),
      enable: announcement.Enable,
      repeatMode: announcement.RepeatMode,
      groupMode: announcement.GroupMode,
      imagePosition: announcement.ImagePosition,
      showGuaranteeLetters: announcement.ShowGuaranteeLetters,
      hasHeaderRow: false,
      excelFile: undefined,
      excelFileName: undefined,
      imageFile: undefined,
      imageFileName: undefined,
      removeImage: false,
      columnHints: [],
    })
    setRawExcel(undefined)

    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const validate = (): string | undefined => {
    if (!state.topic?.trim()) return 'Başlık zorunludur.'
    if (!state.description?.trim()) return 'Açıklama zorunludur.'
    if (!state.startDate || !state.endDate) return 'Başlangıç ve bitiş tarihi zorunludur.'
    if (moment(state.endDate).isBefore(state.startDate)) return 'Bitiş tarihi başlangıç tarihinden önce olamaz.'
    if (!state.editId && !state.excelFile) return 'Excel dosyası zorunludur.'
    return undefined
  }

  const save = async () => {
    const error = validate()
    if (error) {
      Swal.fire({ icon: 'warning', title: error, allowOutsideClick: true })
      return
    }

    const request = {
      Id: state.editId,
      Topic: state.topic.trim(),
      DescriptionTemplate: state.description,
      StartDate: state.startDate,
      EndDate: state.endDate,
      Enable: state.enable,
      RepeatMode: state.repeatMode,
      GroupMode: state.groupMode,
      ImagePosition: state.imagePosition,
      Image: state.imageFile ?? null,
      RemoveImage: state.removeImage,
      Excel: state.excelFile ?? null,
      HasHeaderRow: state.hasHeaderRow,
      ShowGuaranteeLetters: state.showGuaranteeLetters,
    }

    try {
      if (state.editId) {
        await service().update(request)
        Swal.fire({ icon: 'success', title: 'Duyuru güncellendi.', allowOutsideClick: true })
        resetForm()
      } else {
        const result = await service().save(request)
        Swal.fire({
          icon: result && result.InvalidRowCount > 0 ? 'warning' : 'success',
          title: 'Duyuru oluşturuldu.',
          text: result
            ? `${result.SavedRowCount} satır kaydedildi, ${result.InvalidRowCount} satır atlandı.`
            : undefined,
          allowOutsideClick: true,
        })
        resetForm()
        setReport(result)
      }

      loadAnnouncements()
    } catch (err: any) {
      showError(err)
    }
  }

  const remove = (announcement: IExcelAnnouncement) => {
    Swal.fire({
      icon: 'question',
      title: 'Duyuru silinsin mi?',
      text: 'Duyuru, satırları ve açbox kayıtları silinir. Yüklenen Excel dosyası da silinir.',
      showCancelButton: true,
      confirmButtonText: 'Sil',
      cancelButtonText: 'Vazgeç',
    }).then(result => {
      if (!result.isConfirmed) return

      service()
        .remove(announcement.Id)
        .then(() => loadAnnouncements())
        .catch(showError)
    })
  }

  const removeFile = (fileId: number) => {
    Swal.fire({
      icon: 'question',
      title: 'Excel dosyası silinsin mi?',
      text: 'Bu dosyadan üretilen satırlar ve açbox kayıtları da silinir.',
      showCancelButton: true,
      confirmButtonText: 'Sil',
      cancelButtonText: 'Vazgeç',
    }).then(result => {
      if (!result.isConfirmed) return

      service()
        .deleteFile(fileId)
        .then(() => loadAnnouncements())
        .catch(showError)
    })
  }

  // -------------------------------------------------------------- Çözümlenmiş veri

  const openRows = (groupBy: ExcelAnnouncementRowGroupBy, announcementId?: number) => {
    setRows([])
    setState({
      showRows: true,
      groupBy,
      selectedGroupKey: undefined,
      rowsRequestId: state.rowsRequestId + 1,
    })
    loadGroups(groupBy, announcementId)
  }

  const loadGroups = (groupBy: ExcelAnnouncementRowGroupBy, announcementId?: number) => {
    service()
      .getRowSummary({ GroupBy: groupBy, AnnouncementId: announcementId ?? null })
      .then(result => setGroups(result ?? []))
      .catch(showError)
  }

  const loadRows = (group: IExcelAnnouncementRowGroup) => {
    setState({ selectedGroupKey: group.Key })

    const filter = {
      GroupBy: state.groupBy,
      AnnouncementId: state.groupBy === ExcelAnnouncementRowGroupBy.Announcement ? Number(group.Key) : null,
      FileId: state.groupBy === ExcelAnnouncementRowGroupBy.File ? Number(group.Key) : null,
      DealerCode: state.groupBy === ExcelAnnouncementRowGroupBy.Dealer ? group.Key : null,
      Page: 1,
      PageSize: 200,
    }

    service()
      .getRows(filter)
      .then(result => setRows(result ?? []))
      .catch(showError)
  }

  // -------------------------------------------------------------- Tablolar

  const announcementColumns = useMemo(
    () => [
      { Header: 'Başlık', accessor: 'col1' },
      { Header: 'Tarih Aralığı', accessor: 'col2' },
      { Header: 'Durum', accessor: 'col3' },
      { Header: 'Tekrar', accessor: 'col4' },
      { Header: 'Sunum', accessor: 'col5' },
      { Header: 'Satır / Bayi', accessor: 'col6' },
      { Header: 'Atlanan', accessor: 'col7' },
      { Header: 'Excel', accessor: 'col8' },
      { Header: 'İşlem', accessor: 'col9' },
    ],
    []
  )

  const announcementData = useMemo(
    () =>
      announcements.map(announcement => ({
        col1: announcement.Topic,
        col2: `${moment(announcement.StartDate).format('DD.MM.YYYY')} — ${moment(announcement.EndDate).format(
          'DD.MM.YYYY'
        )}`,
        col3: (
          <b style={{ color: announcement.Enable ? 'green' : 'red' }}>{announcement.Enable ? 'Açık' : 'Kapalı'}</b>
        ),
        col4: repeatModeOptions.find(option => option.value === announcement.RepeatMode)?.label,
        col5: groupModeOptions.find(option => option.value === announcement.GroupMode)?.label,
        col6: `${announcement.TotalRowCount} / ${announcement.DealerCount}`,
        col7: announcement.InvalidRowCount,
        col8: announcement.Files?.map(file => (
          <div key={file.Id}>
            <a href={file.DownloadUrl} target='_blank' rel='noreferrer'>
              {file.FileName}
            </a>
            <a role='button' className='ms-2 text-danger' title='Dosyayı sil' onClick={() => removeFile(file.Id)}>
              <i className='icon-trash-2' />
            </a>
          </div>
        )),
        col9: (
          <span style={{ whiteSpace: 'nowrap' }}>
            <a
              role='button'
              title='Bayi verilerini görüntüle'
              className='me-2'
              onClick={() => openRows(ExcelAnnouncementRowGroupBy.Announcement, announcement.Id)}
            >
              <i className='icon-eye' />
            </a>
            <a role='button' title='Düzenle' className='me-2' onClick={() => openEdit(announcement)}>
              <i className='icon-edit' />
            </a>
            <a role='button' title='Sil' onClick={() => remove(announcement)}>
              <i className='icon-trash-2' />
            </a>
          </span>
        ),
      })),
    [announcements]
  )

  const rowColumns = useMemo(
    () => [
      { Header: 'Satır', accessor: 'col1' },
      { Header: 'Bayi', accessor: 'col2' },
      { Header: 'Başlık', accessor: 'col3' },
      { Header: "Popup'ta göreceği metin", accessor: 'col4' },
      { Header: 'Önizleme', accessor: 'col5' },
    ],
    []
  )

  const rowData = useMemo(
    () =>
      rows.map(row => ({
        col1: row.RowNumber,
        col2: (
          <>
            {row.DealerCode}
            {row.DealerName && (
              <>
                <br />
                <small>{row.DealerName}</small>
              </>
            )}
          </>
        ),
        col3: row.Topic,
        col4: <span style={{ whiteSpace: 'pre-wrap' }}>{row.Description}</span>,
        col5: (
          <a role='button' title='Popup önizleme' onClick={() => setState({ showPreview: true, previewRow: row })}>
            <i className='icon-eye' />
          </a>
        ),
      })),
    [rows]
  )

  // -------------------------------------------------------------- Görünüm

  return (
    <div className='content col-lg-12 m-auto pb-5'>
      {/* ----------------------------------------------------- Ekle / düzenle */}
      <div className='card'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <span className='h4 mb-0'>{state.editId ? 'Excel Duyurusunu Düzenle' : 'Excel ile Duyuru Ekle'}</span>
          {state.editId > 0 && (
            <IPButton
              type='button'
              className='btn btn-secondary'
              text='Yeni Duyuru'
              onClick={() => {
                setReport(undefined)
                resetForm()
              }}
            />
          )}
        </div>

        <div className='card-body'>
          <div className='alert alert-info' role='alert'>
            Excel'in <b>A sütunu bayi kodudur</b> (baştaki sıfırlar olsun ya da olmasın fark etmez). Diğer sütunlara
            başlık ve açıklama içinden <b>#B#</b>, <b>#C#</b> gibi kolon harfleriyle atıf yapabilirsiniz. Bu duyurular
            bayinin ana sayfasında popup olarak çıkar ve açbox listesine düşer; <b>mobil bildirim gönderilmez</b>.
          </div>

          {/* Tarihler ve iki yükleme butonu tek satırda; alt hizalarından hizalanıyorlar. */}
          <div className='row mt-3 align-items-end'>
            <div className={`col-6 col-md-3 col-xl-2 ${compactDateField}`}>
              <InputCampaign
                type='datetime-local'
                id='ExcelAnnouncementStartDate'
                name='ExcelAnnouncementStartDate'
                label='Başlangıç Tarihi'
                value={toDateTimeLocal(state.startDate)}
                onChange={event => event.target.value && setState({ startDate: new Date(event.target.value) })}
                isActive
              />
            </div>
            <div className={`col-6 col-md-3 col-xl-2 ${compactDateField}`}>
              <InputCampaign
                type='datetime-local'
                id='ExcelAnnouncementEndDate'
                name='ExcelAnnouncementEndDate'
                label='Bitiş Tarihi'
                value={toDateTimeLocal(state.endDate)}
                onChange={event => event.target.value && setState({ endDate: new Date(event.target.value) })}
                isActive
              />
            </div>

            <div className='col-auto mt-3 mt-md-0'>
              <label className='form-label'>Resim (opsiyonel)</label>
              <IPFile
                id='excelAnnouncementImage'
                label='Resim Ekle'
                handleFiles={() => undefined}
                onChange={onImageSelected}
                accept='image/*'
              />
            </div>
            <div className='col-auto mt-3 mt-md-0'>
              <label className='form-label'>
                Excel Dosyası {state.editId > 0 && '(yeni dosya eklemek için seçin)'}
              </label>
              <IPFile
                id='excelAnnouncementFile'
                label='Excel Ekle'
                excelIcon
                handleFiles={() => undefined}
                onChange={onExcelSelected}
                accept='.xlsx,.xls'
              />
            </div>
          </div>

          {(state.imageFileName || state.excelFileName) && (
            <div className='row mt-2'>
              <div className='col-12'>
                <small className='text-muted'>
                  {state.imageFileName && <span className='me-3'>Resim: {state.imageFileName}</span>}
                  {state.excelFileName && <span>Excel: {state.excelFileName}</span>}
                </small>
              </div>
            </div>
          )}

          {/* Başlık ve açıklama solda; onları biçimlendiren seçenekler aynı hizadan başlayıp
              sağda alt alta gidiyor. */}
          <div className='row mt-3'>
            <div className='col-md-8'>
              <label className='form-label'>Başlık</label>
              <IPInput
                type='text'
                maxLength={250}
                value={state.topic}
                onChange={event => setState({ topic: event.target.value })}
              />

              <label className='form-label mt-3'>Açıklama</label>
              <textarea
                className='form-control'
                rows={10}
                style={{ resize: 'vertical', minHeight: '250px' }}
                value={state.description}
                onChange={event => setState({ description: event.target.value })}
                placeholder={'Sevgili #A# bayisi, #B# tarihinde yaptığınız #C# tutarındaki siparişiniz...'}
              />
            </div>

            <div className='col-md-4 mt-3 mt-md-0'>
              <label className='form-label'>Popup Tekrarı</label>
              <IPSelectBox
                options={repeatModeOptions as any}
                value={selected(repeatModeOptions, state.repeatMode)}
                onChangeSingle={option => setState({ repeatMode: Number(option.value) })}
                menuPortalTarget={document.body}
                styles={compactSelectStyles}
              />

              <label className='form-label mt-3'>Aynı Bayinin Birden Fazla Satırı</label>
              <IPSelectBox
                options={groupModeOptions as any}
                value={selected(groupModeOptions, state.groupMode)}
                onChangeSingle={option => setState({ groupMode: Number(option.value) })}
                menuPortalTarget={document.body}
                styles={compactSelectStyles}
              />

              <label className='form-label mt-3'>Resim Konumu</label>
              <IPSelectBox
                options={imagePositionOptions as any}
                value={selected(imagePositionOptions, state.imagePosition)}
                onChangeSingle={option => setState({ imagePosition: Number(option.value) })}
                menuPortalTarget={document.body}
                styles={compactSelectStyles}
              />

              <div className='d-flex flex-column mt-3' style={{ gap: '12px' }}>
                <IPCheckbox
                  label='İlk satır başlık satırı'
                  checked={state.hasHeaderRow}
                  onChange={() => setState({ hasHeaderRow: !state.hasHeaderRow })}
                />
                <IPCheckbox
                  label='Aktif'
                  checked={state.enable}
                  onChange={() => setState({ enable: !state.enable })}
                />
                <IPCheckbox
                  label='Teminat duyurusu (mektupları popupta göster)'
                  checked={state.showGuaranteeLetters}
                  onChange={() => setState({ showGuaranteeLetters: !state.showGuaranteeLetters })}
                />
                {state.editId > 0 && (
                  <IPCheckbox
                    label='Mevcut resmi kaldır'
                    checked={state.removeImage}
                    onChange={() => setState({ removeImage: !state.removeImage })}
                  />
                )}
              </div>
            </div>
          </div>

          {state.columnHints.length > 0 && (
            <div className='row mt-3'>
              <div className='col-12'>
                <div className='border rounded p-3' style={{ backgroundColor: '#fbfbfb', fontSize: '13px' }}>
                  <b>Kolon rehberi</b> — açıklamada bu ifadeleri kullanabilirsiniz:
                  <div className='table-responsive mt-2'>
                    <table className='table table-sm table-bordered mb-0 bg-white'>
                      <thead>
                        <tr>
                          {state.columnHints.map(hint => (
                            <th key={hint.column} style={{ backgroundColor: '#f1f3f5' }}>
                              #{hint.column}#
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {state.columnHints.map(hint => (
                            <td key={`sample-${hint.column}`}>{hint.sample}</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className='row mt-3 justify-content-end'>
            <div className='col-md-2'>
              <IPButton
                type='button'
                className='btn btn-success'
                text={state.editId ? 'Duyuruyu Güncelle' : 'Duyuruyu Kaydet'}
                onClick={save}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ----------------------------------------------------- Duyuru listesi */}
      <div className='card mt-4'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <span className='h4 mb-0'>Excel Duyuruları</span>
          <IPButton
            type='button'
            className='btn btn-secondary'
            style={{ width: 'auto', padding: '0 20px' }}
            text='Bayi Verilerini Görüntüle'
            onClick={() => openRows(ExcelAnnouncementRowGroupBy.Announcement)}
          />
        </div>

        <div className='card-body'>
          {report && report.InvalidRows?.length > 0 && (
            <div className='alert alert-warning'>
              <b>Atlanan satırlar:</b>
              <ul className='mb-0'>
                {report.InvalidRows.map(invalid => (
                  <li key={`${invalid.RowNumber}-${invalid.RawDealerCode}`}>
                    {invalid.RowNumber}. satır ({invalid.RawDealerCode || '-'}): {invalid.ErrorText}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Table columns={announcementColumns} data={announcementData} currentPage={0} />
        </div>
      </div>

      {/* ----------------------------------------------------- Bayi verileri */}
      {state.showRows && (
        <div
          ref={rowsSectionRef}
          className='card mt-4'
          style={{ border: '2px solid #d21649', scrollMarginTop: '16px' }}
        >
          <div
            className='card-header d-flex justify-content-between align-items-center'
            style={{ backgroundColor: '#d21649', color: '#fff' }}
          >
            <span className='h4 mb-0'>Bayilere Gidecek Duyurular</span>
            <IPButton
              type='button'
              className='btn btn-secondary'
              style={{ width: 'auto', padding: '0 20px' }}
              text='Kapat'
              onClick={() => setState({ showRows: false })}
            />
          </div>

          <div className='card-body'>
            <div className='row'>
              <div className='col-md-3'>
                <label className='form-label'>Gruplama</label>
                <IPSelectBox
                  options={groupByOptions as any}
                  value={selected(groupByOptions, state.groupBy)}
                  onChangeSingle={option => {
                    const groupBy = Number(option.value)
                    setRows([])
                    setState({ groupBy, selectedGroupKey: undefined })
                    loadGroups(groupBy)
                  }}
                  menuPortalTarget={document.body}
                  styles={compactSelectStyles}
                />
              </div>
            </div>

            <div className='row mt-3'>
              <div className='col-md-4'>
                <div className='list-group' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                  {groups.length === 0 && <div className='list-group-item'>Kayıt yok.</div>}
                  {groups.map(group => (
                    <a
                      key={group.Key}
                      role='button'
                      className={`list-group-item list-group-item-action ${
                        state.selectedGroupKey === group.Key ? 'active' : ''
                      }`}
                      onClick={() => loadRows(group)}
                    >
                      <div className='d-flex justify-content-between'>
                        <span>{group.Label}</span>
                        <span className='badge bg-secondary'>{group.RowCount}</span>
                      </div>
                      {group.SubLabel && <small>{group.SubLabel}</small>}
                    </a>
                  ))}
                </div>
              </div>

              <div className='col-md-8'>
                {state.selectedGroupKey === undefined ? (
                  <div className='text-center mt-4'>Soldan bir grup seçin.</div>
                ) : (
                  <Table columns={rowColumns} data={rowData} currentPage={0} />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ----------------------------------------------------- Popup önizleme */}
      <IPModal
        show={state.showPreview}
        onClose={() => setState({ showPreview: false })}
        hideConfirm
        title={state.previewRow?.Topic}
        width={600}
        height='auto'
      >
        <div className='p-3' style={{ whiteSpace: 'pre-wrap' }}>
          {state.previewRow?.Description}
        </div>
      </IPModal>
    </div>
  )
}
